import { isEqual } from 'lodash';

/**
 * Keep the SAME object for a calendar event whose content did not change.
 *
 * `getCalendarLeanEvents` rebuilds every lean event whenever any of its inputs
 * changes, so editing ONE message handed every chip a new `event` object and
 * defeated the chip's React.memo: measured on a 108-chip month, one message
 * edit re-rendered all 108 chips (547 component renders). The events that did
 * not change are deep-equal to the previous run's; returning the previous
 * object for those lets only the edited chip render again.
 *
 * Keyed per OCCURRENCE (`_id` + instant), because a recurring series expands
 * into many events that share one `_id`. Equality is lodash `isEqual` — exact
 * structural equality, the same test `useAppSelector` already applies — so a
 * reused object is never stale: it is reused only when it is equal in every
 * field. Holds one generation (the previous run), so it never grows.
 */
export function createLeanEventReuser<T extends { _id?: unknown; dueDate?: unknown }>() {
    let previous = new Map<string, T>();

    const keyOf = (e: T): string => {
        const due = e.dueDate instanceof Date ? e.dueDate.getTime() : new Date(e.dueDate as string).getTime();
        return `${String(e._id)}@${Number.isFinite(due) ? due : String(e.dueDate)}`;
    };

    return (next: T[]): T[] => {
        const current = new Map<string, T>();
        const out = next.map(event => {
            const key = keyOf(event);
            const before = previous.get(key);
            const kept = before !== undefined && isEqual(before, event) ? before : event;
            // A duplicate key within one run keeps its own object; the map only
            // needs one candidate for the next run.
            if (!current.has(key)) current.set(key, kept);
            return kept;
        });
        previous = current;
        return out;
    };
}
